import * as Sentry from '@sentry/react';
import { get, identity, mapValues, pickBy } from 'lodash';
import type { ParametricSelector } from 'reselect';
import Url from 'url-parse';

import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';
import { tokenMap } from '@swf/core/utils/accountResolver';

import type { Brand, Site } from '../enums';

export interface ResolvedTag {
  url: string;
  token?: string;
  signature?: string;
}

export interface ResolverState {
  [tag: string]: ResolvedTag;
}

export interface FetchTagPayload {
  tag: string;
  path?: string;
  open?: boolean;
  query?: object;
  tokenType?: number;
  brand?: Brand;
  site?: Site;
  license?: number;
}

interface ResolverModel extends ModelConfig<ResolverState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const reducers = {
  response: (state: ResolverState, payload: ResolverState) => ({ ...state, ...payload })
};

const effects = {
  async fetchTag(
    { tokenType, tag, query = {}, site }: FetchTagPayload,
    { dispatch, request, rootState }: EffectParams
  ) {
    const brand = select.cms.getBrandId(rootState).toString();
    try {
      const { body } = await request({
        service: 'tags.resolve',
        body: pickBy(
          {
            tag,
            tokenType: tokenType === undefined ? tokenMap[tag as keyof typeof tokenMap] : tokenType,
            urlParam: Url.qs.stringify(query)
          },
          identity
        ),
        meta: pickBy({ brand, site }, identity)
      });

      const url = new Url(
        body.url.startsWith('external-') ? body.url.substring(9) : body.url,
        true
      );
      if (body.parameters) {
        body.parameters = mapValues(body.parameters, (parameter) => encodeURIComponent(parameter));
      }
      url.set('query', { ...query, ...body.parameters, ...url.query });
      dispatch.resolver.response({ [tag]: { url: url.toString(), ...body.parameters } });
    } catch (err) {
      // @TODO handle error
      Sentry.captureException(err);
    }
  }
};

const selectors = (slice: Slicer<ResolverState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getTag() {
    return createSelector(
      slice,
      (rootState: RootState, tag: string) => tag,
      (state: ResolverState, tag: string) => state[tag]
    );
  },
  getUrl(this: { getTag: ParametricSelector<RootState, string, ResolvedTag | undefined> }) {
    return createSelector(this.getTag, (tag): string | undefined => get(tag, 'url'));
  }
});

const resolver: ResolverModel = {
  state: {},
  reducers,
  effects,
  selectors
};

export default resolver;
